import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	makeClassName,
	Modal,
	Row,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import {
	BillingAdjustment,
	Contract,
	ContractInstallment,
	ContractLineItem,
	PaymentLineItem,
	Student,
	StudentNameFragment,
} from "../../../../Utilities";
import { ContractFormat, StudentFormat } from "../../../Format";
import { ContractUtilities } from "../../../Utilities";
import { ControlledContractModal } from "../Modal";
import { ContractPaymentProgressDonut } from "../PaymentProgressDonut";
import { ContractPaymentProgressText } from "../PaymentProgressText";
import styles from "./styles.module.css";

export type CardContract = Pick<Contract, "id" | "name" | "status" | "effectiveDate" | "dateVoided"> & {
	student: Pick<Student, "id"> & StudentNameFragment;
	contractLineItems: (CardContractLineItem & { childContractLineItems: CardContractLineItem[] })[];
	contractInstallments: (Pick<ContractInstallment, "id" | "dueDate" | "portion"> & {
		invoices: {
			paymentLineItems: Pick<PaymentLineItem, "amount">[];
			billingAdjustments: Pick<BillingAdjustment, "amount">[];
		}[];
	})[];
};

export type CardContractLineItem = Pick<ContractLineItem, "id" | "isDiscount" | "amount">;

export type ContractCardProps = {
	variant: "admin" | "parent";
	contract: CardContract;
} & Partial<ClassNameProps & StyleProps>;

export function ContractCard({ variant, contract, className, style }: ContractCardProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const paymentProgress = React.useMemo(() => ContractUtilities.calculatePaymentProgress(contract), [contract]);

	return (
		<React.Fragment>
			<Row justify="spaced-start" className={makeClassName(styles["card"], className)} style={style}>
				<ContractPaymentProgressDonut paymentProgress={paymentProgress} />

				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ flexGrow: 1, minWidth: 0 }}>
					<Row justify="space-between" align="center" style={{ marginBottom: "0.25rem" }}>
						<TruncatedText className={styles["name"]}>{ContractFormat.name(contract)}</TruncatedText>

						<Button variant="link" size="small" onClick={openModal}>
							View
						</Button>
					</Row>

					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem" className={styles["summary-info"]}>
						<Icon.Student size={14} /> <TruncatedText>{StudentFormat.name(contract.student)}</TruncatedText>
					</Row>

					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem" className={styles["summary-info"]}>
						<Icon.CalendarBlank size={14} /> {ContractFormat.Fields.effectiveDate(contract.effectiveDate)}
					</Row>

					<TruncatedText>
						<ContractPaymentProgressText paymentProgress={paymentProgress} />
					</TruncatedText>
				</Column>
			</Row>

			<Modal.If condition={isModalOpen}>
				<ControlledContractModal variant={variant} contractID={contract.id} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}
